import { type Virtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, type RefObject } from 'react';
import { type OptimisticMessage } from '@avanta/shared/types/chat';

interface UseScrollToLastMessageOptions {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  messages: OptimisticMessage[];
  channelId: string | null;
  shouldVirtualize: boolean;
}

export const useScrollToLastMessage = ({
  scrollContainerRef,
  virtualizer,
  messages,
  channelId,
  shouldVirtualize,
}: UseScrollToLastMessageOptions): void => {
  const lastScrolledChannelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!channelId || messages.length === 0) return;
    if (lastScrolledChannelRef.current === channelId) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      if (shouldVirtualize) {
        virtualizer.scrollToIndex(messages.length - 1, { align: 'end' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    });

    lastScrolledChannelRef.current = channelId;
  }, [channelId, messages.length, scrollContainerRef, virtualizer, shouldVirtualize]);
};
